import React, { useEffect, useRef, useState } from 'react';
import { Canvas, IText, Rect, FabricImage } from 'fabric';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Type, Image as ImageIcon, Square, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function MenuEditor({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [fontSize, setFontSize] = useState(24);
  const [textColor, setTextColor] = useState('#ffffff');
  const [bgColor, setBgColor] = useState('#1a1a1a');

  useEffect(() => {
    const fabricCanvas = new Canvas(canvasRef.current, {
      width: 540,
      height: 960,
      backgroundColor: bgColor
    });
    
    setCanvas(fabricCanvas);

    return () => {
      fabricCanvas.dispose();
    };
  }, []);

  useEffect(() => {
    if (canvas) {
      canvas.set({ backgroundColor: bgColor });
      canvas.renderAll();
    }
  }, [bgColor, canvas]);

  const addText = () => {
    if (!canvas || !textInput) return;
    
    const text = new IText(textInput, {
      left: 100,
      top: 100,
      fontSize: fontSize,
      fill: textColor,
      fontFamily: 'Manrope'
    });
    
    canvas.add(text);
    canvas.setActiveObject(text);
    setTextInput('');
  };

  const addRectangle = () => {
    if (!canvas) return;
    
    const rect = new Rect({
      left: 100,
      top: 100,
      width: 200,
      height: 100,
      fill: textColor,
      stroke: '#ffffff',
      strokeWidth: 2
    });
    
    canvas.add(rect);
    canvas.setActiveObject(rect);
  };

  const handleImageUpload = (e) => {
    if (!canvas) return;
    
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      FabricImage.fromURL(event.target.result).then((img) => {
        img.scaleToWidth(300);
        img.set({
          left: 100,
          top: 100
        });
        canvas.add(img);
        canvas.setActiveObject(img);
      });
    };
    reader.readAsDataURL(file);
  };

  const deleteSelected = () => {
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.remove(activeObject);
    }
  };

  const handleSave = () => {
    if (!canvas) return;
    
    const json = canvas.toJSON();
    const dataUrl = canvas.toDataURL({
      format: 'png',
      quality: 1
    });
    
    onSave({
      json: json,
      preview: dataUrl
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="bg-zinc-800 p-4 rounded-lg">
          <canvas ref={canvasRef} className="border-2 border-zinc-700 rounded" />
        </div>
        <div className="mt-4 flex justify-between">
          <Button onClick={onCancel} variant="outline" className="border-zinc-700">
            Anuluj
          </Button>
          <div className="flex space-x-2">
            <Button onClick={deleteSelected} variant="outline" className="border-red-900 text-red-400">
              <Trash2 className="w-4 h-4 mr-2" />
              Usuń zaznaczone
            </Button>
            <Button onClick={handleSave} className="btn-primary">
              <Save className="w-4 h-4 mr-2" />
              Zapisz menu
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <Tabs defaultValue="text" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-zinc-800">
            <TabsTrigger value="text">Tekst</TabsTrigger>
            <TabsTrigger value="image">Obraz</TabsTrigger>
            <TabsTrigger value="shape">Kształt</TabsTrigger>
          </TabsList>

          <TabsContent value="text" className="space-y-4">
            <div>
              <Label>Tekst</Label>
              <Input
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Wpisz tekst..."
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Rozmiar czcionki</Label>
              <Input
                type="number"
                value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Kolor tekstu</Label>
              <Input
                type="color"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                className="bg-zinc-800 border-zinc-700 h-12"
              />
            </div>
            <Button onClick={addText} className="w-full" disabled={!textInput}>
              <Type className="w-4 h-4 mr-2" />
              Dodaj tekst
            </Button>
          </TabsContent>

          <TabsContent value="image" className="space-y-4">
            <div>
              <Label>Wgraj obraz</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <p className="text-sm text-zinc-400">
              Wgraj obraz produktu lub logo do menu
            </p>
          </TabsContent>

          <TabsContent value="shape" className="space-y-4">
            <div>
              <Label>Kolor kształtu</Label>
              <Input
                type="color"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                className="bg-zinc-800 border-zinc-700 h-12"
              />
            </div>
            <Button onClick={addRectangle} className="w-full">
              <Square className="w-4 h-4 mr-2" />
              Dodaj prostokąt
            </Button>
          </TabsContent>
        </Tabs>

        <div className="pt-4 border-t border-zinc-800">
          <Label>Kolor tła</Label>
          <Input
            type="color"
            value={bgColor}
            onChange={(e) => setBgColor(e.target.value)}
            className="bg-zinc-800 border-zinc-700 h-12 mt-2"
          />
        </div>

        <div className="pt-4 border-t border-zinc-800 text-sm text-zinc-400 space-y-2">
          <p><strong className="text-white">Wskazówki:</strong></p>
          <ul className="list-disc list-inside space-y-1">
            <li>Przeciągnij elementy aby je przesunąć</li>
            <li>Użyj uchwytów aby zmienić rozmiar</li>
            <li>Kliknij element aby go zaznaczyć</li>
          </ul>
        </div>
      </div>
    </div>
  );
}